import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { isUiMobile } from '@/redux/modules/browser';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLocale } from '@/redux/modules/intl';
import { useTrackEnterLiveAuctionPressedAnalytics } from '@/utils/amplitude/analytics';
import { useNewBiddingConsoleExperiment } from '@/utils/amplitude/experiments/hooks/useNewBiddingConsole.experiment';
import { popupCenter } from '@/utils/popupCenter';
import { getLiveBiddingUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    children: React.ReactNode;
    onNavigate?: () => void;
    /**
     * When true, fires `Enter Live Auction Pressed` before opening the console.
     * Used by catalog-page entry points (and any other surface that should emit the event).
     */
    shouldTrackEnterLiveAuction?: boolean;
};

const ALT_WIDTH = 1400;
const ALT_HEIGHT = 1248;
const WIDTH = 1248;
const HEIGHT = 800;

/**
 * Turn any react component into a button that opens the bidding console in a new window.
 */
export const BidConsoleOpener = ({
    catalogId = 0,
    children = '',
    onNavigate,
    shouldTrackEnterLiveAuction = false,
}: Props) => {
    const hasNewBiddingConsole = useNewBiddingConsoleExperiment();
    const trackEnterLiveAuctionPressed = useTrackEnterLiveAuctionPressedAnalytics(catalogId);

    const navigate = useNavigate();
    const locale = useAppSelector(getLocale);
    const mobile = useAppSelector(isUiMobile);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));

    const localePath = locale === 'en-gb' ? `/${locale}` : '';

    // Refs keep callbacks current even if a parent memo comparator skips a re-render.
    const onNavigateRef = React.useRef(onNavigate);
    const shouldTrackRef = React.useRef(shouldTrackEnterLiveAuction);
    const trackEnterLiveAuctionPressedRef = React.useRef(trackEnterLiveAuctionPressed);
    onNavigateRef.current = onNavigate;
    shouldTrackRef.current = shouldTrackEnterLiveAuction;
    trackEnterLiveAuctionPressedRef.current = trackEnterLiveAuctionPressed;

    const openBidConsole = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (shouldTrackRef.current) {
                trackEnterLiveAuctionPressedRef.current();
            }
            onNavigateRef.current?.();
            /**
             * Enable either mobile devices or holding down the shift key to open the console in the same window.
             */
            if (Boolean(mobile) || event.shiftKey) {
                navigate(getLiveBiddingUrl(catalogId));
            } else {
                popupCenter({
                    height: hasNewBiddingConsole ? ALT_HEIGHT : HEIGHT,
                    title,
                    url: localePath + getLiveBiddingUrl(catalogId),
                    width: hasNewBiddingConsole ? ALT_WIDTH : WIDTH,
                });
            }
        },
        [mobile, navigate, catalogId, hasNewBiddingConsole, title, localePath]
    );

    if (React.isValidElement(children)) {
        return React.cloneElement(React.Children.only(children), {
            /* @ts-expect-error `children` could be anything */
            onClick: openBidConsole,
        });
    }

    return null;
};
